import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  Schedule as AgedIcon,
  Warning as WarningIcon,
  CheckCircle as PaidIcon,
  Error as OverdueIcon,
  Person as CustomerIcon
} from '@mui/icons-material';

import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/exportUtils';

// Types
interface AgedDebtorCustomer {
  customer_id: number;
  customer_name: string;
  customer_email?: string;
  total_outstanding: number;
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_over_90: number;
  total_outstanding_formatted: string;
  current_formatted: string;
  days_1_30_formatted: string;
  days_31_60_formatted: string;
  days_61_90_formatted: string;
  days_over_90_formatted: string;
  oldest_invoice_days: number;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface AgedDebtorsData {
  report_date: string;
  customers: AgedDebtorCustomer[];
  summary: {
    total_outstanding: number;
    current: number;
    days_1_30: number;
    days_31_60: number;
    days_61_90: number;
    days_over_90: number;
    total_outstanding_formatted: string;
    current_formatted: string;
    days_1_30_formatted: string;
    days_31_60_formatted: string;
    days_61_90_formatted: string;
    days_over_90_formatted: string;
    customer_count: number;
    overdue_customer_count: number;
  };
}

interface AgedDebtorsReportProps {
  startDate: Date;
  endDate: Date;
  loading: boolean;
  onExport: (format: string) => void;
}

const AgedDebtorsReport: React.FC<AgedDebtorsReportProps> = ({
  startDate,
  endDate,
  loading,
  onExport
}) => {
  const [reportData, setReportData] = useState<AgedDebtorsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAgedDebtorsData();
  }, [endDate]); // Aged debtors uses end date as report date

  const fetchAgedDebtorsData = async () => {
    try {
      setError(null);
      const reportDateStr = endDate.toISOString().split('T')[0];

      const response = await fetch(
        `/api/v1/reports/advanced/aged-debtors?as_of_date=${reportDateStr}`
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        throw new Error('Failed to fetch aged debtors data');
      }
    } catch (error) {
      console.error('Error fetching aged debtors data:', error);
      setError('Failed to load aged debtors report');
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'error';
      default: return 'default';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return <PaidIcon />;
      case 'MEDIUM': return <WarningIcon />;
      case 'HIGH': return <OverdueIcon />;
      default: return <CustomerIcon />;
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Loading Aged Debtors Report...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        No outstanding debtor data available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Report Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Aged Debtors Report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            As of {new Date(reportData.report_date).toLocaleDateString('en-US')}
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CustomerIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Outstanding</Typography>
                  <Typography variant="h6">{reportData.summary.total_outstanding_formatted}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {reportData.summary.customer_count} customers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="textSecondary">Current</Typography>
                <Typography variant="h6" color="success.main">{reportData.summary.current_formatted}</Typography>
                <Typography variant="caption" color="textSecondary">Not overdue</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="textSecondary">1-30 Days</Typography>
                <Typography variant="h6" color="info.main">{reportData.summary.days_1_30_formatted}</Typography>
                <Typography variant="caption" color="textSecondary">Recently overdue</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="textSecondary">31-60 Days</Typography>
                <Typography variant="h6" color="warning.main">{reportData.summary.days_31_60_formatted}</Typography>
                <Typography variant="caption" color="textSecondary">Moderate risk</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="textSecondary">61-90 Days</Typography>
                <Typography variant="h6" color="error.main">{reportData.summary.days_61_90_formatted}</Typography>
                <Typography variant="caption" color="textSecondary">High risk</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="textSecondary">90+ Days</Typography>
                <Typography variant="h6" color="error.dark">{reportData.summary.days_over_90_formatted}</Typography>
                <Typography variant="caption" color="textSecondary">Critical</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Risk Summary Alert */}
      {reportData.summary.overdue_customer_count > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Action Required:</strong> {reportData.summary.overdue_customer_count} customer{reportData.summary.overdue_customer_count !== 1 ? 's have' : ' has'} overdue invoices.
            Total overdue amount: {(parseFloat(reportData.summary.total_outstanding_formatted.replace(/[$,]/g, '')) - parseFloat(reportData.summary.current_formatted.replace(/[$,]/g, ''))).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </Typography>
        </Alert>
      )}

      {/* Detailed Aged Debtors Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell align="right"><strong>Total Outstanding</strong></TableCell>
              <TableCell align="right"><strong>Current</strong></TableCell>
              <TableCell align="right"><strong>1-30 Days</strong></TableCell>
              <TableCell align="right"><strong>31-60 Days</strong></TableCell>
              <TableCell align="right"><strong>61-90 Days</strong></TableCell>
              <TableCell align="right"><strong>90+ Days</strong></TableCell>
              <TableCell align="center"><strong>Risk Level</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.customers.map((customer) => (
              <TableRow key={customer.customer_id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {customer.customer_name}
                    </Typography>
                    {customer.customer_email && (
                      <Typography variant="caption" color="textSecondary">
                        {customer.customer_email}
                      </Typography>
                    )}
                    {customer.oldest_invoice_days > 0 && (
                      <Typography variant="caption" color="error" display="block">
                        Oldest: {customer.oldest_invoice_days} days
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {customer.total_outstanding_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={customer.current > 0 ? 'success.main' : 'textSecondary'}>
                    {customer.current_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={customer.days_1_30 > 0 ? 'info.main' : 'textSecondary'}>
                    {customer.days_1_30_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={customer.days_31_60 > 0 ? 'warning.main' : 'textSecondary'}>
                    {customer.days_31_60_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={customer.days_61_90 > 0 ? 'error.main' : 'textSecondary'}>
                    {customer.days_61_90_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={customer.days_over_90 > 0 ? 'error.dark' : 'textSecondary'}>
                    {customer.days_over_90_formatted}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={getRiskIcon(customer.risk_level)}
                    label={customer.risk_level}
                    color={getRiskColor(customer.risk_level) as any}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}

            {/* Summary Row */}
            <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold">TOTAL</Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold">
                  {reportData.summary.total_outstanding_formatted}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {reportData.summary.current_formatted}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold" color="info.main">
                  {reportData.summary.days_1_30_formatted}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold" color="warning.main">
                  {reportData.summary.days_31_60_formatted}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold" color="error.main">
                  {reportData.summary.days_61_90_formatted}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <Typography variant="body1" fontWeight="bold" color="error.dark">
                  {reportData.summary.days_over_90_formatted}
                </Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Collection Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recommended Actions
          </Typography>
          <Grid container spacing={2}>
            {reportData.summary.days_1_30 > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>1-30 Days Overdue:</strong> Send friendly payment reminders to customers with recent overdue invoices.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {reportData.summary.days_31_60 > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>31-60 Days Overdue:</strong> Follow up with phone calls and consider payment plans for moderate risk accounts.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {(reportData.summary.days_61_90 > 0 || reportData.summary.days_over_90 > 0) && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>60+ Days Overdue:</strong> Escalate to collections, consider legal action, and review credit terms for high-risk customers.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgedDebtorsReport;
